const DEFAULTS = {
	threshold: 0,
	rootMargin: '0px',
	stagger: 100,
};

const TYPES = [
	'fade',
	'fade-up',
	'fade-down',
	'fade-left',
	'fade-right',
	'zoom-in',
	'zoom-out',
];

const SELF_SELECTOR = TYPES.map((t) => `.animate-${t}`).join(', ');
const CHILDREN_SELECTOR = TYPES.map((t) => `.animate-children-${t}`).join(', ');
const STAGGER_RE = /^animate-stagger-(\d+)$/;
const DELAY_RE = /^animate-delay-(\d+)$/;
const EXPANDED_FLAG = 'animateChildrenExpanded';

const prefersReducedMotion = () =>
	window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const getType = (el) => {
	for (const type of TYPES) {
		if (el.classList.contains(`animate-${type}`)) return type;
	}
	return null;
};

const getChildrenType = (el) => {
	for (const type of TYPES) {
		if (el.classList.contains(`animate-children-${type}`)) return type;
	}
	return null;
};

const readClassValue = (el, regex, fallback) => {
	for (const cls of el.classList) {
		const m = cls.match(regex);
		if (m) return parseInt(m[1], 10);
	}
	return fallback;
};

class EntranceAnimation {
	constructor(options = {}) {
		this.options = { ...DEFAULTS, ...options };
		this.observers = new Map();
	}

	init() {
		const targets = document.querySelectorAll(
			`${SELF_SELECTOR}, ${CHILDREN_SELECTOR}`
		);

		if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
			targets.forEach((el) => this.reveal(el));
			return;
		}

		this.expandChildrenGroups();
		this.prepareTargets();
		this.observe();
	}

	expandChildrenGroups() {
		document.querySelectorAll(CHILDREN_SELECTOR).forEach((group) => {
			if (group.dataset[EXPANDED_FLAG] === 'true') return;

			const type = getChildrenType(group);
			if (!type) return;

			const stagger = readClassValue(
				group,
				STAGGER_RE,
				this.options.stagger
			);
			const baseDelay = readClassValue(group, DELAY_RE, 0);

			Array.from(group.children).forEach((child, index) => {
				if (getType(child)) return;

				child.classList.add(`animate-${type}`);
				const delay = baseDelay + index * stagger;
				if (delay > 0) {
					child.style.setProperty('--anim-delay', `${delay}ms`);
				}
			});

			group.dataset[EXPANDED_FLAG] = 'true';
		});
	}

	prepareTargets() {
		document.querySelectorAll(SELF_SELECTOR).forEach((el) => {
			el.classList.add('anim');

			const delay = readClassValue(el, DELAY_RE, 0);
			if (delay > 0 && !el.style.getPropertyValue('--anim-delay')) {
				el.style.setProperty('--anim-delay', `${delay}ms`);
			}
		});
	}

	observe() {
		document
			.querySelectorAll(`${SELF_SELECTOR}`)
			.forEach((el) => this.observerFor(el).observe(el));
	}

	observerFor() {
		const key = 'default';
		if (!this.observers.has(key)) {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							this.reveal(entry.target);
							observer.unobserve(entry.target);
						}
					});
				},
				{
					threshold: this.options.threshold,
					rootMargin: this.options.rootMargin,
				}
			);
			this.observers.set(key, observer);
		}
		return this.observers.get(key);
	}

	reveal(el) {
		el.classList.add('is-visible');
	}

	static markReady() {
		if (!prefersReducedMotion()) {
			document.documentElement.classList.add('anim-ready');
		}
	}

	refresh() {
		this.expandChildrenGroups();
		this.prepareTargets();
		this.observe();
	}

	static init(options) {
		const instance = new EntranceAnimation(options);
		instance.init();
		return instance;
	}
}

export default EntranceAnimation;
